// The roll mode of an ability check (2024): Advantage and Disadvantage sources from armor (Stealth), Poisoned,
// Frightened, Remarkable Athlete, Large Form, buffs and Rage. Pure, shared by the sheet and the server (DM roll
// requests). Moved unchanged from Skills.js.
import { hasLargeForm } from './speciesTraits.mjs';
import { isRageEffective } from './barbarianRage.mjs';
import { championAthleticsSources } from './champion.mjs';
import { armorStealthSources } from './abilityChecks.mjs';
import { hasActiveEffect } from './combat/activeEffects.mjs';
import { getBuffCheckAdvantageSources, getBuffCheckDisadvantageSources } from './effects/buffModifiers.mjs';
import { frightenedEffects } from './frightened.mjs';
import { normalizeEquipmentMap } from './equipmentSlots.mjs';

export const resolveD20RollMode = ({ advantageSources = [], disadvantageSources = [] } = {}) => {
  const hasAdvantage = Array.isArray(advantageSources) && advantageSources.length > 0;
  const hasDisadvantage = Array.isArray(disadvantageSources) && disadvantageSources.length > 0;
  if (hasAdvantage && !hasDisadvantage) return 'advantage';
  if (hasDisadvantage && !hasAdvantage) return 'disadvantage';
  return 'normal';
};

export const resolveAbilityCheckRollMode = (character, abilityKey, options = {}) => {
  const normalizedAbility = String(abilityKey || '').toLowerCase();
  const advantageSources = Array.isArray(options.advantageSources)
    ? [...options.advantageSources]
    : [];
  const disadvantageSources = Array.isArray(options.disadvantageSources)
    ? [...options.disadvantageSources]
    : [];

  const equippedItems = character?.equipment && typeof character.equipment === 'object'
    ? Object.values(normalizeEquipmentMap(character.equipment)).filter(Boolean)
    : Array.isArray(character?.armor) ? character.armor.filter(Boolean) : [];
  disadvantageSources.push(...armorStealthSources(equippedItems, normalizedAbility, options.skillKey));

  const targetId = options.targetCombatantId ?? character?.characterId ?? character?._id;
  const localPoisoned = [...(character?.conditions || []),...(character?.statusConditions || [])].some(c=>String(c?.name ?? c?.label ?? c?.id ?? c).toLowerCase().replace(/^condition:/,'')==='poisoned');
  if (localPoisoned || hasActiveEffect(options.combatState?.activeEffects, String(targetId), 'condition:poisoned')) {
    disadvantageSources.push('Poisoned');
  }
  // Frightened: Disadvantage on ability checks (the sheet has no map, so the source is taken to be in sight).
  if (frightenedEffects(character, options.combatState, String(targetId)).length) disadvantageSources.push('Frightened');

  advantageSources.push(...championAthleticsSources(character,normalizedAbility,options.skillKey));
  if (['str', 'strength'].includes(normalizedAbility) && hasLargeForm(options.combatState, String(targetId))) advantageSources.push('Large Form');
  advantageSources.push(...getBuffCheckAdvantageSources(options.combatState, options.targetCombatantId ?? character?.characterId ?? character?._id, normalizedAbility));
  disadvantageSources.push(...getBuffCheckDisadvantageSources(options.combatState, options.targetCombatantId ?? character?.characterId ?? character?._id, normalizedAbility));
  if (normalizedAbility === 'str' && isRageEffective(character,options.combatState,targetId)) {
    advantageSources.push('Rage');
  }

  return {
    mode: resolveD20RollMode({ advantageSources, disadvantageSources }),
    advantageSources,
    disadvantageSources,
  };
};
